import React from 'react';
import './VisualizationCard.css';

interface StorageUsageCardProps {
  storageUsedGB: number;
  maxStorageGB?: number;
}

const StorageUsageCard: React.FC<StorageUsageCardProps> = ({ storageUsedGB, maxStorageGB = 10 }) => {
  const percentage = Math.min((storageUsedGB / maxStorageGB) * 100, 100);
  
  // Color logic: green (0-5GB), amber (5-9GB), red (9GB+)
  const getColor = () => {
    if (storageUsedGB <= 5) return '#10b981'; // green
    if (storageUsedGB < 9) return '#f59e0b'; // amber
    return '#dc3545'; // red
  };

  const showUpgradePrompt = storageUsedGB >= 9;

  // Speedometer gauge using SVG
  const gaugeAngle = (percentage / 100) * 180; // 0-180 degrees
  const needleRotation = gaugeAngle - 90; // Adjust for SVG coordinate system

  return (
    <div className="visualization-card">
      <h3 className="card-title">Storage Usage</h3>
      <div className="card-content">
        <div 
          className="speedometer-gauge"
          role="meter"
          aria-valuenow={storageUsedGB}
          aria-valuemin={0}
          aria-valuemax={maxStorageGB}
          aria-label={`${storageUsedGB.toFixed(1)} GB out of ${maxStorageGB} GB storage used`}
        >
          <svg viewBox="0 0 200 120" className="gauge-svg">
            {/* Background arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="#e0e0e0"
              strokeWidth="20"
              strokeLinecap="round"
            />
            {/* Colored arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke={getColor()}
              strokeWidth="20"
              strokeLinecap="round"
              strokeDasharray={`${(percentage / 100) * 251.2} 251.2`}
            />
            {/* Needle */}
            <line
              x1="100"
              y1="100"
              x2="100"
              y2="40"
              stroke="#333"
              strokeWidth="3"
              strokeLinecap="round"
              transform={`rotate(${needleRotation} 100 100)`}
            />
            {/* Center dot */}
            <circle cx="100" cy="100" r="5" fill="#333" />
          </svg>
        </div>
        <div className="usage-text">
          {storageUsedGB.toFixed(1)} GB / {maxStorageGB} GB
        </div>
        {showUpgradePrompt && (
          <div className="upgrade-prompt">
            Storage almost full! Upgrade for more space.
          </div>
        )}
      </div>
    </div>
  );
};

export default StorageUsageCard;
